import React from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  LayoutDashboard, Eye, Users, GraduationCap, BookOpen,
  FileText, Calendar, Shield, Heart, ChevronLeft, LogOut, Settings,
  Sparkles, Baby
} from 'lucide-react';
import { cn } from '../../../lib/utils';
import { isAuthenticated } from '../../../lib/auth';
import { useIsMobileV41 } from '../../../components/dna-mobile/DnaResponsive';
import AdminMobileShell from '../../../components/admin-mobile/AdminMobileShell';

const NAV = [
  { label: 'Dashboard', path: '/v2/infantil', icon: LayoutDashboard },
  { label: 'Observações', path: '/v2/infantil/observacoes', icon: Eye },
  { label: 'Novo Registro', path: '/v2/infantil/novo-registro', icon: Sparkles },
  { divider: true, label: 'VISÕES' },
  { label: 'Criança 360', path: '/v2/infantil/crianca', icon: Baby },
  { label: 'Turma 360', path: '/v2/infantil/turma', icon: Users },
  { divider: true, label: 'PRODUÇÃO' },
  { label: 'Portfólios', path: '/v2/infantil/portfolios', icon: BookOpen },
  { label: 'Relatórios', path: '/v2/infantil/relatorios', icon: FileText },
  { label: 'Planejamento', path: '/v2/infantil/planejamento', icon: Calendar },
  { divider: true, label: 'GESTÃO' },
  { label: 'Coordenação', path: '/v2/infantil/coordenacao', icon: Shield },
  { label: 'Família', path: '/v2/infantil/familia', icon: Heart },
];

export default function Infantil360Shell() {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobileV41(1024);

  if (!isAuthenticated()) {
    navigate('/login');
    return null;
  }

  // Web administrativo no celular: mesmo shell mobile do /v2; seções do módulo em lista rolável própria.
  if (isMobile) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#F7F8FC' }}>
        <AdminMobileShell>
          <main className="adm-m-main" data-testid="infantil360-content">
            <nav className="adm-m-subnav" aria-label="Seções do DNA Infantil 360 IA" data-testid="infantil-m-subnav">
              {NAV.filter((i) => !i.divider).map((item) => (
                <NavLink key={item.path} to={item.path} end className={({ isActive }) => cn('adm-m-chip', isActive && 'on')}
                  data-testid={`infantil-m-nav-${item.path.split('/').pop()}`}>{item.label}</NavLink>
              ))}
            </nav>
            <Outlet />
          </main>
        </AdminMobileShell>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: '#F7F8FC' }}>
      {/* Sidebar */}
      <aside className="w-[260px] flex-shrink-0 flex flex-col fixed left-0 top-0 h-screen z-50 overflow-hidden"
        style={{ background: 'linear-gradient(180deg, #2E1A47 0%, #1A0F2E 100%)' }}>
        {/* Logo */}
        <div className="h-16 flex items-center px-5 gap-3 flex-shrink-0">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #2BB673, #1a8f5a)' }}>
            <Baby className="h-5 w-5 text-white" />
          </div>
          <div>
            <p className="text-sm font-bold text-white/90">DNA Infantil</p>
            <p className="text-[9px] text-white/40 uppercase tracking-widest">360 IA</p>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto px-3 py-2" style={{ scrollbarWidth: 'none' }}>
          {NAV.map((item, idx) => {
            if (item.divider) {
              return <p key={idx} className="text-[8px] font-bold uppercase tracking-[0.2em] text-white/20 mt-4 mb-2 px-3">{item.label}</p>;
            }
            const Icon = item.icon;
            const active = location.pathname === item.path;
            return (
              <NavLink key={item.path} to={item.path} data-testid={`infantil-nav-${item.path.split('/').pop()}`}
                className={cn(
                  "flex items-center gap-3 px-3 py-2.5 rounded-xl mb-0.5 transition-all text-[13px] font-medium",
                  active
                    ? "bg-white/10 text-white"
                    : "text-white/40 hover:text-white/70 hover:bg-white/[0.04]"
                )}>
                {active && (
                  <motion.div layoutId="infantilNav" className="absolute left-0 w-[3px] h-5 rounded-r-full bg-[#2BB673]"
                    style={{ boxShadow: '0 0 12px #2BB67380' }}
                    transition={{ type: 'spring', stiffness: 400, damping: 35 }} />
                )}
                <Icon className={cn("h-4 w-4 flex-shrink-0", active && "text-[#2BB673]")} />
                <span>{item.label}</span>
              </NavLink>
            );
          })}
        </nav>

        {/* Back to DNACore */}
        <div className="p-3 border-t border-white/[0.05] flex-shrink-0">
          <button onClick={() => navigate('/v2/dashboard')} data-testid="infantil-back-btn"
            className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-white/30 hover:text-white/60 hover:bg-white/[0.04] transition-all text-[12px]">
            <ChevronLeft className="h-4 w-4" /> Voltar ao DNACore
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="ml-[260px] flex-1 min-h-screen">
        <header className="h-16 flex items-center justify-between px-8 bg-white border-b border-slate-100">
          <div className="flex items-center gap-2">
            <span className="text-lg font-bold text-[#2E1A47]">Educação Infantil</span>
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#DDF7EC] text-[#2BB673]">360 IA</span>
          </div>
          <div className="flex items-center gap-3 text-sm text-slate-500">
            <span>2026</span>
            <span className="text-slate-300">|</span>
            <span>1º Trimestre</span>
          </div>
        </header>
        <main className="p-8" data-testid="infantil360-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
